/**
 * SendComposer.tsx — EN→DE confirmed send (Design Spec §05, D-12, ARCH §8.1).
 *
 * Two-step gate so nothing goes out unread:
 *   1. Chatter types English in the always-visible composer; optionally flags PPV + price.
 *   2. "Translate & preview DE" → POST /conversations/:id/translate-preview.
 *   3. An inline preview card appears ABOVE the composer (never a modal — the thread
 *      stays in view): German prominent + EDITABLE (owner choice), English beneath.
 *   4. "Confirm & send in German" dispatches; "Edit" returns to the English source.
 *
 * Hard-block (ARCH §8.1, T-04-36): a failed translation has NO send path — the only
 * exit is retry. There is no raw-English send.
 *
 * Media (D-FREE-MEDIA, owner 2026-06-29): vault media can be attached to ANY send, not
 * just PPV. With a price → paid PPV (chat_product). With NO price → a free gift
 * (content.type:"media"). Sending media for free is gated behind a confirm dialog so
 * content is never given away by forgetting to set a price. Media with no caption sends
 * directly (no translation step — there is nothing to translate).
 *
 * Disabled on dead/needs-reauth accounts (D-06, T-04-38).
 */

import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { messagesApi, ApiRequestError } from '../lib/api-client.js';
import type { TranslatePreviewDto } from '../lib/api-client.js';
import { Button, Textarea, Badge, Dialog, useToast } from '@crm/ui';
import { isAccountDead } from './AccountHealthBadge.js';
import { VaultPickerModal, ThumbImage } from './VaultPickerModal.js';
import { vaultApi } from '../lib/api-client.js';

/**
 * Max vault media attachments per send. MUST stay in sync with the server cap
 * (MAX_MEDIA_PER_SEND in apps/api/src/modules/messages/send.service.ts) — the server
 * rejects an over-cap set with a 400, so keeping the UI cap identical avoids a
 * confirm-then-reject round trip.
 */
const MAX_MEDIA_PER_SEND = 10;

/**
 * A vault media attachment held in the composer. uploadId/type/width/height are the send-body
 * fields; folderId + hasThumbnail are display-only (render the EU-proxy thumbnail in the chip).
 */
interface AttachedMedia {
  uploadId: string;
  type: string;
  width: number;
  height: number;
  folderId: string;
  hasThumbnail: boolean;
}

/** Neutral kind icon shown when a thumbnail is unavailable (no poster / proxy fetch failed). */
function MediaKindIcon({ type }: { type: string }) {
  return type === 'video' ? (
    <svg className="w-4 h-4 text-text-muted" fill="currentColor" viewBox="0 0 20 20">
      <path d="M6.3 2.84A1 1 0 005 3.75v12.5a1 1 0 001.3.96l10-4.17a1 1 0 000-1.84l-10-4.17z" />
    </svg>
  ) : (
    <svg className="w-4 h-4 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

/**
 * Retry seed for a failed outbound send (pending-send bubble → "Retry"). Loads the
 * failed text back into the composer and re-enters the normal translate-preview →
 * confirm flow with a FRESH clientSendId — never a replay of the dead outbound_send
 * row (whose clientSendId the server would idempotently swallow without re-sending).
 */
export interface RetrySeed {
  /** Monotonic key — a new key re-applies the seed even for identical text. */
  key: number;
  en: string;
  isPpv: boolean;
  priceAmountMinor: number | null;
  /** The failed send carried vault media — it must be re-attached by hand. */
  hadMedia: boolean;
}

interface SendComposerProps {
  conversationId: string;
  accountHealthState: string | null;
  onMessageSent?: () => void;
  /** Account ID for vault browsing (required for VaultPickerModal). */
  accountId?: string;
  /** Fan's Maloum member ID for purchase badges in vault picker (D-05-13). */
  fanMemberId?: string;
  /**
   * Migration 0014 — when false the fan is in English mode: the composer skips the EN→DE
   * translate-preview and sends the typed English verbatim in one step. Default true.
   */
  autoTranslate?: boolean;
  /** Failed-send retry request from the thread (applied once per key). */
  retrySeed?: RetrySeed | null;
}

type ComposerState =
  | { stage: 'compose' }
  | { stage: 'translating' }
  | { stage: 'translation_failed'; errorMsg: string }
  | { stage: 'preview'; preview: TranslatePreviewDto }
  | { stage: 'sending'; preview: TranslatePreviewDto }
  | { stage: 'sent' };

export function SendComposer({
  conversationId,
  accountHealthState,
  onMessageSent,
  accountId,
  fanMemberId,
  autoTranslate = true,
  retrySeed = null,
}: SendComposerProps) {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [englishText, setEnglishText] = React.useState('');
  const [editedDe, setEditedDe] = React.useState('');
  const [composerState, setComposerState] = React.useState<ComposerState>({ stage: 'compose' });

  // PPV mode — Maloum settles PPV in EUR only, so currency is fixed.
  const [isPpv, setIsPpv] = React.useState(false);
  const [ppvAmount, setPpvAmount] = React.useState('');
  // EUR-only platform (review #7): the server hard-rejects non-EUR, so there is no currency
  // picker — every PPV is priced in EUR (the € prefix on the input reflects this).
  const ppvCurrency = 'EUR';

  // Vault media attachments (Plan 05-07 F3; multi-media migration 0030). A send may carry
  // several vault media — the array accumulates as the chatter attaches from the picker.
  const [vaultPickerOpen, setVaultPickerOpen] = React.useState(false);
  const [attachedMedia, setAttachedMedia] = React.useState<AttachedMedia[]>([]);

  const isDead = isAccountDead(accountHealthState);
  const clientSendId = React.useRef<string>(crypto.randomUUID());

  const isPreviewing = composerState.stage === 'preview' || composerState.stage === 'sending';
  const composeDisabled = composerState.stage === 'translating' || isPreviewing;
  const ppvPriceInvalid = isPpv && (!ppvAmount || parseFloat(ppvAmount) <= 0);

  // Media / free-media derivations (D-FREE-MEDIA).
  const hasMedia = attachedMedia.length > 0;
  const mediaCount = attachedMedia.length;
  const hasText = englishText.trim().length > 0;
  /** Free media = an attachment with NO price (a giveaway). PPV = attachment WITH a price. */
  const isFreeMedia = hasMedia && !isPpv;
  /** PPV must carry an attachment — the server builds chat_product, which requires media. */
  const ppvNeedsMedia = isPpv && !hasMedia;
  /** Media with no caption sends directly (nothing to translate). */
  const mediaOnly = hasMedia && !hasText;

  // Free-media confirm gate (owner 2026-06-29): a no-price media send is a deliberate
  // giveaway, so confirm before dispatch. The pending dispatcher is stashed and fired on
  // "Yes, send for free"; any other send (text, PPV) bypasses the gate.
  const [freeMediaConfirmOpen, setFreeMediaConfirmOpen] = React.useState(false);
  const pendingSendRef = React.useRef<(() => void) | null>(null);

  /** Run a send dispatch directly, or route it through the free-media confirm dialog. */
  function dispatchSend(run: () => void) {
    if (hasMedia && !isPpv) {
      pendingSendRef.current = run;
      setFreeMediaConfirmOpen(true);
    } else {
      run();
    }
  }

  function cancelFreeMedia() {
    pendingSendRef.current = null;
    setFreeMediaConfirmOpen(false);
  }

  function confirmFreeMedia() {
    setFreeMediaConfirmOpen(false);
    const run = pendingSendRef.current;
    pendingSendRef.current = null;
    run?.();
  }

  /** Shared post-send reset (used by every send mutation). */
  function resetComposerAfterSend() {
    setEnglishText('');
    setEditedDe('');
    setIsPpv(false);
    setPpvAmount('');
    setAttachedMedia([]);
    clientSendId.current = crypto.randomUUID();
    setComposerState({ stage: 'compose' });
    queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
    onMessageSent?.();
  }

  /** Shared send-error toast. */
  function toastSendError(err: unknown) {
    const msg =
      err instanceof ApiRequestError
        ? typeof err.body.message === 'string'
          ? err.body.message
          : err.body.message.join('; ')
        : (err as Error).message ?? 'Send failed';
    addToast(`Send failed: ${msg}`, 'danger');
  }

  /** Build the optional PPV price payload from the current price inputs. */
  function ppvPrice() {
    return isPpv && ppvAmount
      ? { amount_minor: Math.round(parseFloat(ppvAmount) * 100), currency: ppvCurrency }
      : undefined;
  }

  /** Build the optional vault media payload from the current attachments (multi-media). */
  function mediaPayload() {
    return hasMedia
      ? {
          // Multi-media (migration 0030): the full set travels as vaultMedia[]. The server
          // supersedes the legacy single fields with this array.
          vaultMedia: attachedMedia.map((m) => ({
            uploadId: m.uploadId,
            type: m.type,
            width: m.width,
            height: m.height,
          })),
          // Free media (attachment, no price) is given away — the server requires explicit
          // intent (confirmFree). A free-media send only reaches a mutation after the
          // "Send media for free?" confirm dialog (dispatchSend), so assert it here.
          ...(!isPpv ? { confirmFree: true } : {}),
        }
      : {};
  }

  // ---------------------------------------------------------------------------
  // Step 1: translation preview
  // ---------------------------------------------------------------------------

  const translateMut = useMutation({
    // The text travels as the mutation VARIABLE (not read from englishText in
    // mutationFn) — same closure-race rule as sendMut below, and it lets the
    // failed-send retry effect fire a translate before its setState commits.
    mutationFn: (textEn: string) => messagesApi.translatePreview(conversationId, textEn),
    onMutate: () => setComposerState({ stage: 'translating' }),
    onSuccess: (dto) => {
      if (dto.translationStatus === 'failed') {
        setComposerState({
          stage: 'translation_failed',
          errorMsg: 'Translation failed. Retry required before sending.',
        });
      } else {
        setEditedDe(dto.previewDe);
        setComposerState({ stage: 'preview', preview: dto });
      }
    },
    onError: (err) => {
      const msg =
        err instanceof ApiRequestError
          ? typeof err.body.message === 'string'
            ? err.body.message
            : err.body.message.join('; ')
          : (err as Error).message ?? 'Translation request failed';
      setComposerState({ stage: 'translation_failed', errorMsg: msg });
    },
  });

  // ---------------------------------------------------------------------------
  // Step 2: confirmed send (regular or PPV)
  // ---------------------------------------------------------------------------

  const sendMut = useMutation({
    // The approved preview is the mutation VARIABLE, not read from composerState:
    // onMutate flips the stage to 'sending', which re-renders and refreshes the
    // observer's options BEFORE mutationFn runs — a stage guard here saw 'sending'
    // and threw ("Cannot send before previewing") on every confirmed send.
    mutationFn: (preview: TranslatePreviewDto) =>
      // Media rides along whenever attached — free (no price) OR PPV (priced).
      messagesApi.confirmSend(conversationId, {
        approvedDe: editedDe,
        clientSendId: clientSendId.current,
        originalEn: preview.originalEn,
        // ③: prove this German send came from the translate step (server requires it for
        // German-mode fans). The previewed row id is on the preview the chatter just approved.
        previewSendId: preview.sendId,
        price: ppvPrice(),
        ...mediaPayload(),
      }),
    onMutate: () => {
      setComposerState((s) => (s.stage === 'preview' ? { stage: 'sending', preview: s.preview } : s));
    },
    onSuccess: () => {
      addToast(
        isPpv
          ? `PPV sent (${ppvAmount} ${ppvCurrency})`
          : isFreeMedia
            ? 'Sent — message + free media queued.'
            : 'Message sent — queued for delivery.',
        'success',
      );
      resetComposerAfterSend();
    },
    onError: (err) => {
      toastSendError(err);
      setComposerState((s) =>
        s.stage === 'sending' && translateMut.data
          ? { stage: 'preview', preview: translateMut.data }
          : { stage: 'compose' },
      );
    },
  });

  // ---------------------------------------------------------------------------
  // English mode (migration 0014): single-step send — no translate-preview. The typed
  // English is sent verbatim (untranslated=true; the server gates this to English-mode
  // fans). PPV still works (price + vault media). CSAM guard still runs server-side.
  // ---------------------------------------------------------------------------

  const englishSendMut = useMutation({
    mutationFn: () => {
      const text = englishText.trim();
      if (!text) throw new Error('Nothing to send');
      // Media rides along whenever attached — free (no price) OR PPV (priced).
      return messagesApi.confirmSend(conversationId, {
        approvedDe: text, // English mode: the sent text IS the English (no translation)
        originalEn: text,
        clientSendId: clientSendId.current,
        untranslated: true,
        price: ppvPrice(),
        ...mediaPayload(),
      });
    },
    onSuccess: () => {
      addToast(
        isPpv
          ? `PPV sent (${ppvAmount} ${ppvCurrency})`
          : isFreeMedia
            ? 'Sent — message + free media queued.'
            : 'Message sent — queued for delivery.',
        'success',
      );
      resetComposerAfterSend();
    },
    onError: toastSendError,
  });

  // ---------------------------------------------------------------------------
  // Failed-send retry (pending-send bubble → "Retry"): load the failed text (+ PPV
  // price) back into the composer and re-run the normal flow. A fresh clientSendId is
  // minted — reusing the failed row's id would be idempotently swallowed server-side
  // (returns the dead row, re-sends nothing). Media is never restored silently: the
  // chatter re-attaches from the vault (the composer's own PPV/media guards enforce it).
  // ---------------------------------------------------------------------------

  const composerRootRef = React.useRef<HTMLDivElement>(null);
  const appliedSeedKey = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (!retrySeed || appliedSeedKey.current === retrySeed.key) return;
    // Don't clobber an in-flight step — once it settles, this effect re-runs
    // (stage is a dep) and applies the still-unconsumed seed.
    if (composerState.stage === 'sending' || composerState.stage === 'translating') return;
    appliedSeedKey.current = retrySeed.key;

    const en = retrySeed.en.trim();
    setEnglishText(retrySeed.en);
    setEditedDe('');
    setAttachedMedia([]);
    setIsPpv(retrySeed.isPpv);
    setPpvAmount(
      retrySeed.priceAmountMinor != null ? (retrySeed.priceAmountMinor / 100).toFixed(2) : '',
    );
    clientSendId.current = crypto.randomUUID();
    setComposerState({ stage: 'compose' });

    if (retrySeed.hadMedia) {
      addToast('Media is not restored on retry — re-attach it from the vault before sending.', 'warning');
    }
    // Plain-text German-mode retry goes straight to the preview step (the §8.1
    // human confirm gate still applies). PPV/media retries stay in compose so the
    // chatter re-attaches first; English mode is a single-step send anyway.
    if (autoTranslate && en && !retrySeed.isPpv && !retrySeed.hadMedia) {
      translateMut.mutate(en);
    }
    requestAnimationFrame(() => {
      composerRootRef.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus();
    });
  }, [retrySeed, composerState.stage, autoTranslate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Media-only send (D-FREE-MEDIA): attachment with NO caption. There is nothing to
  // translate, so this skips the translate-preview step entirely and posts the media
  // directly — free (content.type:"media") or PPV (chat_product) depending on the price.
  // Works for any fan mode (no text → no raw-English concern).
  // ---------------------------------------------------------------------------

  const mediaOnlySendMut = useMutation({
    mutationFn: () => {
      if (!hasMedia) throw new Error('No media attached');
      return messagesApi.confirmSend(conversationId, {
        approvedDe: '', // media-only: no caption
        originalEn: '',
        clientSendId: clientSendId.current,
        price: ppvPrice(),
        ...mediaPayload(),
      });
    },
    onSuccess: () => {
      addToast(
        isPpv ? `PPV sent (${ppvAmount} ${ppvCurrency})` : 'Free media sent — queued for delivery.',
        'success',
      );
      resetComposerAfterSend();
    },
    onError: toastSendError,
  });

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (isDead) {
    return (
      <div className="border-t border-border px-4 py-3 bg-bg-card">
        <p className="text-xs text-danger text-center">
          Account is dead or needs re-authentication — sending is disabled (D-06).
        </p>
      </div>
    );
  }

  return (
    <>
      {accountId && (
        <VaultPickerModal
          open={vaultPickerOpen}
          onClose={() => setVaultPickerOpen(false)}
          accountId={accountId}
          fanMemberId={fanMemberId}
          maxSelectable={Math.max(0, MAX_MEDIA_PER_SEND - attachedMedia.length)}
          onAttach={(picked) => {
            // Append the newly-picked items to the existing set (dedup by uploadId, cap at MAX).
            setAttachedMedia((prev) => {
              const merged = [...prev];
              for (const m of picked) {
                if (!merged.some((x) => x.uploadId === m.uploadId)) merged.push(m);
              }
              if (merged.length > MAX_MEDIA_PER_SEND) {
                addToast(`You can attach up to ${MAX_MEDIA_PER_SEND} media per message.`, 'warning');
                return merged.slice(0, MAX_MEDIA_PER_SEND);
              }
              return merged;
            });
            setVaultPickerOpen(false);
          }}
        />
      )}

      {/* Free-media confirm (D-FREE-MEDIA): a no-price media send is a deliberate giveaway. */}
      <Dialog open={freeMediaConfirmOpen} onClose={cancelFreeMedia} title="Send media for free?">
        <div className="p-4 flex flex-col gap-4">
          <p className="text-sm text-text-muted">
            You're about to send{' '}
            <span className="font-semibold text-text">
              {mediaCount} media item{mediaCount === 1 ? '' : 's'}
            </span>{' '}
            to this fan with <span className="font-semibold text-text">no price</span> — they'll receive
            {mediaCount === 1 ? ' it' : ' them'} for free. Are you sure?
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={cancelFreeMedia}>
              Cancel
            </Button>
            <Button variant="success" size="sm" onClick={confirmFreeMedia}>
              Yes, send for free
            </Button>
          </div>
        </div>
      </Dialog>

      <div ref={composerRootRef} className="border-t border-border bg-bg-card">
        {/* ── Preview card (above composer) — German mode only ──────────── */}
        {autoTranslate && isPreviewing && (
          <div className="m-3 mb-0 rounded-xl border border-accent/40 bg-accent/5 p-3 flex flex-col gap-2.5 max-h-[45vh]">
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              <span className="inline-flex items-center text-[10px] font-mono font-semibold text-accent border border-accent/40 rounded px-1 py-px leading-none">
                DE
              </span>
              <span className="text-xs font-semibold text-text">
                Fan will receive this — confirm before sending
              </span>
              {isPpv && (
                <Badge variant="success" className="text-xs">
                  PPV · {ppvAmount || '?'} {ppvCurrency}
                  {hasMedia ? ` · ${mediaCount} media` : ''}
                </Badge>
              )}
              {isFreeMedia && (
                <Badge variant="outline" className="text-xs">
                  Free media · {mediaCount} item{mediaCount === 1 ? '' : 's'}
                </Badge>
              )}
            </div>

            {/* German — prominent + editable (owner choice) */}
            <textarea
              value={editedDe}
              onChange={(e) => setEditedDe(e.target.value)}
              rows={3}
              disabled={composerState.stage === 'sending'}
              aria-label="German message (editable before sending)"
              className="w-full max-h-[28vh] bg-transparent text-text text-base font-medium leading-snug resize-y rounded focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:opacity-60"
            />

            {/* English source (reference) — scrolls so a long source can't push the
                confirm/edit action row below the fold */}
            <div className="flex items-start gap-1.5 border-t border-accent/20 pt-2 max-h-20 min-h-0 overflow-y-auto">
              <span className="inline-flex items-center text-[10px] font-mono font-semibold text-text-subtle border border-border rounded px-1 py-px leading-none mt-px">
                EN
              </span>
              <span className="text-xs text-text-muted">{composerState.preview.originalEn}</span>
            </div>

            {ppvPriceInvalid && (
              <p className="text-xs text-warning">PPV requires a valid price before sending.</p>
            )}
            {ppvNeedsMedia && (
              <p className="text-xs text-warning">
                PPV requires attached media — choose “Edit” and attach from the vault.
              </p>
            )}

            <div className="flex items-center justify-between gap-2 shrink-0">
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => {
                    // Capture the approved preview at click time (the free-media dialog
                    // may defer the actual dispatch past a state change).
                    if (composerState.stage !== 'preview') return;
                    const preview = composerState.preview;
                    dispatchSend(() => sendMut.mutate(preview));
                  }}
                  loading={composerState.stage === 'sending'}
                  disabled={!editedDe.trim() || ppvPriceInvalid || ppvNeedsMedia}
                >
                  {isPpv
                    ? `Confirm & send PPV (${ppvAmount || '?'} ${ppvCurrency})`
                    : isFreeMedia
                      ? 'Confirm & send (with free media)'
                      : 'Confirm & send in German'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setComposerState({ stage: 'compose' })}
                  disabled={composerState.stage === 'sending'}
                >
                  Edit
                </Button>
              </div>
              <span className="text-xs text-text-subtle">Translated by Maloum</span>
            </div>
          </div>
        )}

        {/* ── Translation failed — HARD BLOCK (ARCH §8.1 / T-04-36) ──────── */}
        {autoTranslate && composerState.stage === 'translation_failed' && (
          <div className="m-3 mb-0 rounded-xl border border-danger/40 bg-danger/10 p-3 flex flex-col gap-2 max-h-[30vh]">
            <p className="text-sm text-danger font-medium shrink-0">
              Translation failed — send blocked
            </p>
            {/* A long API error scrolls here instead of eating the viewport. */}
            <p className="text-xs text-danger/80 min-h-0 overflow-y-auto">
              {composerState.errorMsg}
            </p>
            <p className="text-xs text-text-muted shrink-0">
              You must retry translation. Sending in English is not permitted (ARCH §8.1).
            </p>
            <div className="flex gap-2 justify-end shrink-0">
              <Button size="sm" variant="ghost" onClick={() => setComposerState({ stage: 'compose' })}>
                Edit message
              </Button>
              <Button
                size="sm"
                onClick={() => translateMut.mutate(englishText.trim())}
                loading={translateMut.isPending}
                disabled={!englishText.trim()}
              >
                Retry translation
              </Button>
            </div>
          </div>
        )}

        {/* ── Composer (always visible) ─────────────────────────────────── */}
        <div className="p-3 flex flex-col gap-2.5">
          {/* No visible "Type in English" label — the placeholder is the single
              hint (owner 2026-07-01, #4). aria-label keeps the accessible name. */}
          <Textarea
            label=""
            aria-label="Type your message in English"
            value={englishText}
            onChange={(e) => setEnglishText(e.target.value)}
            disabled={composeDisabled}
            placeholder="Type your message in English…"
            rows={2}
            // Bound resize-y dragging so the composer can never grow past the
            // viewport and push its own send button off-screen. min-h needs the
            // important suffix — the primitive's min-h-[80px] wins the cascade.
            className="min-h-[56px]! max-h-[35vh]"
          />

          {/* Attached media chips — one per item (free media OR PPV; multi-media 0030). */}
          {hasMedia && (
            <div className="flex flex-col gap-1.5">
              {attachedMedia.map((m, i) => (
                <div
                  key={m.uploadId}
                  className="flex items-center gap-2 p-2 bg-bg-input border border-border rounded-md text-xs"
                >
                  <div className="relative w-12 h-12 bg-bg-card rounded-md overflow-hidden shrink-0 flex items-center justify-center">
                    {/* Real thumbnail via the EU media proxy (same source as the picker); the
                        neutral kind icon is the fallback when there is no poster or the fetch
                        fails (expired signed URL / CSAM gate). Videos show a poster + play badge. */}
                    {accountId && m.hasThumbnail ? (
                      <ThumbImage
                        src={vaultApi.mediaThumbUrl(accountId, m.folderId, m.uploadId)}
                        fallback={<MediaKindIcon type={m.type} />}
                      />
                    ) : (
                      <MediaKindIcon type={m.type} />
                    )}
                    {m.type === 'video' && accountId && m.hasThumbnail && (
                      <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="bg-scrim rounded-full w-5 h-5 flex items-center justify-center">
                          <svg className="w-3 h-3 text-accent-fg" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6.3 2.84A1 1 0 005 3.75v12.5a1 1 0 001.3.96l10-4.17a1 1 0 000-1.84l-10-4.17z" />
                          </svg>
                        </span>
                      </span>
                    )}
                  </div>
                  <span className="flex-1 text-text truncate">
                    {m.type === 'video' ? 'Video' : 'Photo'}
                    {mediaCount > 1 ? ` ${i + 1} of ${mediaCount}` : ' attached'}
                    {isFreeMedia && <span className="text-accent font-medium"> · sends free</span>}
                  </span>
                  <button
                    className="rounded text-text-muted hover:text-danger transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
                    onClick={() =>
                      setAttachedMedia((prev) => prev.filter((x) => x.uploadId !== m.uploadId))
                    }
                    aria-label={`Remove ${m.type} attachment`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* Attach-from-vault — ALWAYS available (free media OR PPV), never gated on price.
              Stays visible while under the cap so more media can be added. */}
          {accountId && mediaCount < MAX_MEDIA_PER_SEND && (
            <Button
              variant="outline"
              size="sm"
              className="self-start"
              onClick={() => setVaultPickerOpen(true)}
              disabled={composeDisabled}
            >
              {hasMedia ? 'Add more media' : 'Attach media from vault'}
            </Button>
          )}
          {ppvNeedsMedia && !composeDisabled && (
            <p className="text-xs text-warning">PPV requires attached media — attach one above.</p>
          )}

          {/* Footer — PPV toggle (+ inline price) · Translate */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-text-muted cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isPpv}
                  onChange={(e) => setIsPpv(e.target.checked)}
                  disabled={composeDisabled}
                  className="w-3.5 h-3.5 accent-accent"
                />
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
                  <rect x="5" y="11" width="14" height="9" rx="2" />
                  <path strokeLinecap="round" d="M8 11V8a4 4 0 018 0v3" />
                </svg>
                PPV message {!isPpv && <span className="text-text-subtle">(set a price)</span>}
              </label>

              {isPpv && (
                <div className="flex items-center gap-1.5">
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-text-subtle pointer-events-none">
                      €
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={ppvAmount}
                      onChange={(e) => setPpvAmount(e.target.value)}
                      onBlur={() => {
                        // Review #6: snap to 2 decimals so the value shown/confirmed equals the
                        // value charged. amount_minor rounds to integer cents, so e.g. 5.999
                        // would charge €6.00 while the label still read 5.999 — normalize here.
                        const n = parseFloat(ppvAmount);
                        if (Number.isFinite(n) && n > 0) {
                          setPpvAmount((Math.round(n * 100) / 100).toFixed(2));
                        }
                      }}
                      disabled={composeDisabled}
                      placeholder="9.99"
                      aria-label="PPV price (EUR)"
                      className="w-24 bg-bg-input text-text border border-border rounded pl-5 pr-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/60"
                    />
                  </div>
                  <span className="text-xs text-text-subtle">EUR</span>
                </div>
              )}
            </div>

            {mediaOnly ? (
              // Media with no caption — direct send (nothing to translate). Free media routes
              // through the confirm dialog; PPV sends directly. Works in either fan mode.
              <Button
                variant="success"
                onClick={() => dispatchSend(() => mediaOnlySendMut.mutate())}
                loading={mediaOnlySendMut.isPending}
                disabled={composeDisabled || ppvPriceInvalid}
              >
                {isPpv ? `Send PPV (${ppvAmount || '?'} ${ppvCurrency})` : 'Send media for free'}
              </Button>
            ) : autoTranslate ? (
              <Button
                onClick={() => translateMut.mutate(englishText.trim())}
                loading={composerState.stage === 'translating'}
                disabled={!englishText.trim() || composeDisabled}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h7M7.5 7v0M9 7c0 4-2.5 7-5 8M6 11c1.8 1.8 3.6 2.6 5 3M13 21l4-9 4 9M14.6 18h4.8" />
                </svg>
                Translate &amp; preview DE
              </Button>
            ) : (
              // English mode (migration 0014): single-step verbatim English send.
              <Button
                variant="success"
                onClick={() => dispatchSend(() => englishSendMut.mutate())}
                loading={englishSendMut.isPending}
                disabled={!englishText.trim() || ppvPriceInvalid || ppvNeedsMedia || isDead}
              >
                {isPpv ? `Send PPV in English (${ppvAmount || '?'} ${ppvCurrency})` : 'Send in English'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
